import { useEffect, useRef, useState, useCallback } from "react";
import { api } from "../services/api";
import { createCable } from "../lib/cable";

export interface NotificationItem {
  id: number;
  kind: string;
  title: string;
  body: string | null;
  data: Record<string, unknown>;
  read_at: string | null;
  created_at: string;
}

export function useNotifications() {
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [loading, setLoading]             = useState(true);
  const cableRef                          = useRef<ReturnType<typeof createCable> | null>(null);

  const unreadCount = notifications.filter((n) => !n.read_at).length;

  const markAllRead = useCallback(async () => {
    await api.patch("/notifications/mark_all_read");
    setNotifications((prev) => prev.map((n) => ({ ...n, read_at: n.read_at ?? new Date().toISOString() })));
  }, []);

  const markKindRead = useCallback(async (kind: string) => {
    const targets = notifications.filter((n) => !n.read_at && n.kind === kind);
    await Promise.all(targets.map((n) => api.patch(`/notifications/${n.id}/mark_read`)));
    const ids = new Set(targets.map((n) => n.id));
    setNotifications((prev) =>
      prev.map((n) => ids.has(n.id) ? { ...n, read_at: new Date().toISOString() } : n)
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notifications]);

  const markRead = useCallback(async (id: number) => {
    await api.patch(`/notifications/${id}/mark_read`);
    setNotifications((prev) =>
      prev.map((n) => n.id === id ? { ...n, read_at: new Date().toISOString() } : n)
    );
  }, []);

  useEffect(() => {
    api.get("/notifications")
      .then((res) => setNotifications(res.data?.notifications ?? []))
      .catch(() => {})
      .finally(() => setLoading(false));

    const cable        = createCable();
    cableRef.current   = cable;

    cable.subscriptions.create("NotificationsChannel", {
      received(data: NotificationItem) {
        setNotifications((prev) => [data, ...prev]);
      },
    });

    return () => {
      cable.disconnect();
    };
  }, []);

  return { notifications, unreadCount, loading, markAllRead, markKindRead, markRead };
}
